import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { CustomResponsesInputs, missingCustom } from '../../components/events/CustomFields';
import { toast } from 'sonner';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Calendar, MapPin, Users, Loader2, IndianRupee, CheckCircle2,
  ScrollText, Info, Tag, CheckCircle, XCircle, Clock, AlertCircle,
} from 'lucide-react';
import { API } from '../../utils/apiConfig';
import {
  fileUrl, categoryColor, TSHIRT_SIZES, GENDERS, FOOD_PREFS, SELF_RATINGS,
  statusLabel, cAuth, todayIST, formatEventDate, regCountdown,
} from '../../components/events/eventUtils';
import { EventBannerCarousel } from '../../components/events/EventBannerCarousel';
import CustomerFooter from '../../components/CustomerFooter';

let cashfreeInstance = null;
const loadCashfree = async () => {
  if (cashfreeInstance) return cashfreeInstance;
  try {
    const { load } = await import('@cashfreepayments/cashfree-js');
    cashfreeInstance = await load({ mode: 'production' });
    return cashfreeInstance;
  } catch (e) { return null; }
};

const emptyPartner = () => ({ name: '', mobile: '', email: '', gender: '', date_of_birth: '', city: '', emergency_contact: '', dupr_id: '', tshirt_size: '' });

function PartnerCard({ category, partner, onChange }) {
  const timerRef = useRef(null);
  const lastLookedUpRef = useRef('');

  const handleMobileChange = (raw) => {
    const cleaned = (raw || '').replace(/\D/g, '').slice(0, 10);
    onChange({ ...partner, mobile: cleaned });
    if (timerRef.current) clearTimeout(timerRef.current);
    if (cleaned.length !== 10 || lastLookedUpRef.current === cleaned) return;
    timerRef.current = setTimeout(async () => {
      lastLookedUpRef.current = cleaned;
      try {
        const res = await axios.get(`${API}/events/partner-lookup`, { params: { mobile: cleaned }, ...cAuth() });
        if (res.data.found && res.data.player?.name) {
          onChange((prev) => ({ ...prev, mobile: cleaned, name: prev.name || res.data.player.name }));
          toast.success('Partner name auto-filled', { id: `partner-lookup-${category.id}` });
        }
      } catch (e) { /* silent */ }
    }, 400);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3" data-testid={`partner-card-${category.id}`}>
      <h2 className="font-semibold text-slate-800">Partner Details — <span className="text-violet-600">{category.name}</span></h2>
      <p className="text-xs text-slate-400">Optional — you can add your partner's details later.</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Mobile Number</Label>
          <Input value={partner.mobile} onChange={(e) => handleMobileChange(e.target.value)} placeholder="10-digit mobile" inputMode="numeric" data-testid={`partner-mobile-${category.id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Name</Label>
          <Input value={partner.name} onChange={(e) => onChange({ ...partner, name: e.target.value })} data-testid={`partner-name-${category.id}`} />
        </div>
      </div>
    </div>
  );
}

// Highlight class for invalid required fields
const fieldCls = (invalid) =>
  invalid ? 'border-red-400 ring-1 ring-red-300 focus:ring-red-400' : '';

export default function PlayerEventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState({});
  const [partners, setPartners] = useState({});
  const [waiver, setWaiver] = useState(false);
  const [custom, setCustom] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [masters, setMasters] = useState({ tshirt_sizes: TSHIRT_SIZES, self_ratings: SELF_RATINGS });

  useEffect(() => {
    axios.get(`${API}/events/public/${id}`, cAuth())
      .then((r) => setEvent(r.data))
      .catch(() => toast.error('Event not found'))
      .finally(() => setLoading(false));
  }, [id]);

  // Load dynamic masters (T-Shirt sizes, Self Ratings)
  useEffect(() => {
    axios.get(`${API}/events/masters/public`)
      .then((r) => setMasters(r.data))
      .catch(() => { /* fallback to defaults */ });
  }, []);

  // Auto-select the single category when there's only one
  useEffect(() => {
    if (!event) return;
    const cats = event.categories || [];
    if (cats.length === 1 && selected.length === 0) {
      setSelected([cats[0].id]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  // Auto-apply the event's active coupon
  useEffect(() => {
    const c = event?.coupon;
    if (!c || !c.code || c.is_active === false || couponCode) return;
    const today = todayIST();
    const inWindow = (!c.valid_from || today >= c.valid_from) && (!c.valid_to || today <= c.valid_to);
    if (inWindow) setCouponCode(c.code);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  // Track link visit on mount; upgrade to "started" on first form interaction
  const [trackStartedFired, setTrackStartedFired] = useState(false);
  const trackActivity = async (stage) => {
    try {
      const cid = customer?.id;
      const body = { stage, customer_id: cid, customer_mobile: customer?.mobile, customer_name: customer?.name };
      await axios.post(`${API}/events/${id}/track-activity`, body);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    if (event) trackActivity('link_visit');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
  const trackStarted = () => {
    if (!trackStartedFired) { setTrackStartedFired(true); trackActivity('started'); }
  };

  useEffect(() => {
    if (!couponCode.trim() || !selected.length) { setCouponResult(null); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/events/${id}/validate-coupon`,
          { code: couponCode.trim(), category_ids: selected }, cAuth());
        if (active) setCouponResult(res.data);
      } catch (e) { /* silent */ }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponCode, selected]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;
  if (!event) return <div className="min-h-screen flex items-center justify-center text-slate-500">Event not found</div>;

  const req = event.required_fields || {};
  const cats = event.categories || [];
  const maxCats = event.max_categories_per_player || 99;
  const partnerCats = cats.filter((c) => selected.includes(c.id) && (c.format === 'doubles' || c.format === 'mixed'));
  const subtotal = cats.filter((c) => selected.includes(c.id)).reduce((s, c) => s + (Number(c.fee) || 0), 0);
  const discount = couponResult?.valid ? couponResult.discount : 0;
  const total = Math.max(0, subtotal - discount);
  const countdown = regCountdown(event.registration_end);

  // Tri-state helpers: "mandatory" | "optional" | true (legacy) = show field
  const showField = (key) => { const v = req[key]; return v === 'mandatory' || v === 'optional' || v === true; };
  const isMandatory = (key) => { const v = req[key]; return v === 'mandatory' || v === true; };

  const activeCoupon = (() => {
    const c = event.coupon;
    if (!c || !c.code || c.is_active === false) return null;
    const today = todayIST();
    const inWindow = (!c.valid_from || today >= c.valid_from) && (!c.valid_to || today <= c.valid_to);
    return inWindow ? c : null;
  })();

  const catDiscountedFee = (fee) => {
    if (!activeCoupon || !fee) return null;
    const disc = activeCoupon.discount_type === 'flat'
      ? Number(activeCoupon.discount_value) || 0
      : (fee * (Number(activeCoupon.discount_value) || 0)) / 100;
    const capped = Math.min(disc, fee);
    if (capped <= 0) return null;
    return Math.round((fee - capped) * 100) / 100;
  };

  const toggleCat = (cid) => {
    setSelected((prev) => {
      if (prev.includes(cid)) return prev.filter((x) => x !== cid);
      if (prev.length >= maxCats) { toast.error(`You can pick up to ${maxCats} categories`); return prev; }
      return [...prev, cid];
    });
  };

  const setPartnerFor = (catId, updater) => {
    setPartners((prev) => {
      const current = prev[catId] || emptyPartner();
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [catId]: next };
    });
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const res = await axios.post(`${API}/events/${id}/validate-coupon`,
        { code: couponCode.trim(), category_ids: selected }, cAuth());
      setCouponResult(res.data);
      if (res.data.valid) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (e) {
      toast.error('Could not validate coupon');
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Validate required fields — returns array of field keys that are missing (only mandatory ones)
  const getMissingFields = () => {
    const missing = [];
    if (isMandatory('gender') && !(extra.gender || customer?.gender)) missing.push('gender');
    if (isMandatory('city') && !(extra.city || customer?.short_address)) missing.push('city');
    if (isMandatory('dob') && !(extra.date_of_birth || customer?.date_of_birth)) missing.push('dob');
    if (isMandatory('emergency_contact') && !extra.emergency_contact) missing.push('emergency_contact');
    if (isMandatory('dupr') && !(extra.dupr_id || customer?.dupr_id)) missing.push('dupr_id');
    if (isMandatory('tshirt') && !extra.tshirt_size) missing.push('tshirt_size');
    if (isMandatory('food') && !extra.food_preference) missing.push('food_preference');
    if (isMandatory('self_rating') && !extra.self_rating) missing.push('self_rating');
    if (isMandatory('waiver') && !waiver) missing.push('waiver');
    return [...missing, ...missingCustom(event.custom_fields, custom)];
  };

  const submit = async () => {
    if (!isAuthenticated) {
      const target = `/events/${id}`;
      localStorage.setItem('post_login_redirect', target);
      navigate(`/login?next=${encodeURIComponent(target)}`);
      return;
    }
    if (!selected.length) { toast.error('Select at least one category'); return; }
    setSubmitAttempted(true);
    const missing = getMissingFields();
    if (missing.length) {
      toast.error('Please complete all required fields highlighted in red');
      return;
    }

    const partnersPayload = partnerCats
      .filter((c) => partners[c.id] && (partners[c.id].name?.trim() || partners[c.id].mobile?.trim()))
      .map((c) => ({ category_id: c.id, partner: partners[c.id] }));

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${id}/self-register`, {
        partners: partnersPayload,
        category_ids: selected,
        coupon_code: couponResult?.valid ? couponCode.trim() : '',
        waiver_accepted: waiver || !req.waiver,
        player_extra: extra,
        custom_responses: custom,
      }, cAuth());
      const reg = res.data.registration;
      if (reg.payment_status === 'paid') {
        toast.success('Registration confirmed!');
        navigate(`/events/payment-status?reg=${reg.id}&done=1`);
        return;
      }
      const order = await axios.post(`${API}/events/payments/create-order`, { registration_id: reg.id }, cAuth());
      if (!order.data.success) { toast.error('Could not start payment'); setSubmitting(false); return; }
      const cf = await loadCashfree();
      if (!cf) { toast.error('Payment gateway unavailable'); setSubmitting(false); return; }
      await cf.checkout({ paymentSessionId: order.data.payment_session_id, redirectTarget: '_self' });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'eligibility_failed') toast.error(d.messages.join(' • '));
      else toast.error(typeof d === 'string' ? d : 'Registration failed');
      setSubmitting(false);
    }
  };

  const missing = submitAttempted ? getMissingFields() : [];
  const isMissing = (key) => missing.includes(key);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-fuchsia-50 pb-48">
      {/* Banner carousel */}
      <div className="relative">
        <EventBannerCarousel banners={event.banners} fallbackFileId={event.banner_file_id} fileUrl={fileUrl} heightClass="h-64 sm:h-80" />
        <button onClick={() => navigate('/events')} className="absolute top-4 left-4 bg-black/40 backdrop-blur text-white rounded-full p-2 z-10" data-testid="event-back-btn"><ArrowLeft className="w-5 h-5" /></button>
      </div>

      <div className="px-4 -mt-6 relative space-y-4">
        <div className="bg-white rounded-2xl shadow-md p-5">
          <h1 className="text-xl font-bold text-slate-900">{event.name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
            {event.venue && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.venue}</span>}
            {event.start_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatEventDate(event.start_date)}
                {event.end_date && event.end_date !== event.start_date ? ` → ${formatEventDate(event.end_date)}` : ''}
              </span>
            )}
            {event.start_time && (
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Starts at {event.start_time}</span>
            )}
            {event.total_registrations > 0 && (
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {event.total_registrations} joined</span>
            )}
          </div>
          {countdown && (
            <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${countdown.days <= 3 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`} data-testid="reg-countdown">
              <Clock className="w-3 h-3" /> {countdown.label}
            </div>
          )}
          {event.description && <p className="text-sm text-slate-600 mt-3">{event.description}</p>}
        </div>

        {/* Gallery */}
        {(event.gallery || []).length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {event.gallery.map((g, i) => <img key={i} src={fileUrl(g.file_id)} alt="g" className="h-24 w-32 object-cover rounded-xl shrink-0" />)}
          </div>
        )}

        {/* Rules */}
        {event.rules && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-2 font-semibold text-slate-800 mb-2"><ScrollText className="w-4 h-4 text-violet-500" /> Rules</div>
            <p className="text-sm text-slate-600 whitespace-pre-line">{event.rules}</p>
          </div>
        )}

        {/* Categories */}
        <div>
          <h2 className="font-semibold text-slate-800 mb-2">Select Categories <span className="text-xs text-slate-400">(up to {maxCats})</span></h2>
          <div className="space-y-2">
            {cats.map((c, i) => {
              const col = categoryColor(i);
              const on = selected.includes(c.id);
              const full = c.slots_left !== null && c.slots_left <= 0;
              const discFee = catDiscountedFee(c.fee);
              return (
                <button key={c.id} onClick={() => toggleCat(c.id)} data-testid={`player-cat-${c.id}`}
                  className={`w-full text-left flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all ${col.bg} ${on ? `${col.border} ring-2 ${col.ring}` : 'border-transparent'}`}>
                  <span className={`w-3 h-3 rounded-full shrink-0 ${col.dot}`} />
                  <div className="flex-1">
                    <div className={`font-semibold ${col.text}`}>{c.name}</div>
                    <div className="text-xs text-slate-500">{statusLabel(c.format)} · {c.slots_left !== null ? `${c.slots_left} slots left` : 'open'}{full ? ' · joins waitlist' : ''}</div>
                  </div>
                  <div className="text-right">
                    {discFee !== null ? (
                      <>
                        <div className="text-xs text-slate-400 line-through flex items-center justify-end" data-testid={`player-cat-mrp-${c.id}`}><IndianRupee className="w-3 h-3" />{c.fee}</div>
                        <div className="font-bold text-emerald-600 flex items-center justify-end" data-testid={`player-cat-discounted-${c.id}`}><IndianRupee className="w-3.5 h-3.5" />{discFee}</div>
                      </>
                    ) : (
                      <div className="font-bold text-slate-800 flex items-center justify-end" data-testid={`player-cat-fee-${c.id}`}><IndianRupee className="w-3.5 h-3.5" />{c.fee}</div>
                    )}
                    {on && <CheckCircle2 className={`w-5 h-5 ${col.text} ml-auto mt-1`} />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Player + required extra fields */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <h2 className="font-semibold text-slate-800">Your Details</h2>
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center text-white font-bold">{customer?.name?.charAt(0)?.toUpperCase() || 'U'}</div>
              <div><p className="font-semibold text-slate-900">{customer?.name}</p><p className="text-sm text-slate-500">+91 {customer?.mobile}</p></div>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-violet-50 rounded-xl p-3" data-testid="login-required-notice">
              <span className="text-sm text-slate-600">Log in to complete your registration</span>
              <Button size="sm" variant="outline" onClick={() => { const t = `/events/${id}`; localStorage.setItem('post_login_redirect', t); navigate(`/login?next=${encodeURIComponent(t)}`); }} data-testid="login-to-continue-btn">
                Login
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {showField('gender') && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Gender {isMandatory('gender') && <span className="text-red-500">*</span>}{isMissing('gender') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                <Select value={extra.gender || customer?.gender || ''} onValueChange={(v) => { setExtra({ ...extra, gender: v }); trackStarted(); }}>
                  <SelectTrigger className={fieldCls(isMissing('gender'))} data-testid="player-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{statusLabel(g)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showField('dob') && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Date of Birth {isMandatory('dob') && <span className="text-red-500">*</span>}{isMissing('dob') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                <Input type="date" value={extra.date_of_birth || customer?.date_of_birth || ''} onChange={(e) => { setExtra({ ...extra, date_of_birth: e.target.value }); trackStarted(); }} className={fieldCls(isMissing('dob'))} data-testid="player-dob" />
              </div>
            )}
            {showField('city') && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">City {isMandatory('city') && <span className="text-red-500">*</span>}{isMissing('city') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                <Input value={extra.city ?? (customer?.short_address || '')} onChange={(e) => { setExtra({ ...extra, city: e.target.value }); trackStarted(); }} className={fieldCls(isMissing('city'))} data-testid="player-city" />
              </div>
            )}
            {showField('emergency_contact') && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Emergency Contact {isMandatory('emergency_contact') && <span className="text-red-500">*</span>}{isMissing('emergency_contact') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                <Input value={extra.emergency_contact || ''} onChange={(e) => { setExtra({ ...extra, emergency_contact: e.target.value }); trackStarted(); }} className={fieldCls(isMissing('emergency_contact'))} data-testid="player-emergency" />
              </div>
            )}
            {showField('dupr') && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">DUPR ID {isMandatory('dupr') && <span className="text-red-500">*</span>}{isMissing('dupr_id') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                  <Input value={extra.dupr_id || customer?.dupr_id || ''} onChange={(e) => { setExtra({ ...extra, dupr_id: e.target.value }); trackStarted(); }} className={fieldCls(isMissing('dupr_id'))} data-testid="player-dupr" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">DUPR Rating</Label>
                  <Input type="number" step="0.01" min="0" max="8" value={extra.dupr_rating ?? (customer?.dupr_rating ?? '')} onChange={(e) => { setExtra({ ...extra, dupr_rating: e.target.value ? parseFloat(e.target.value) : undefined }); trackStarted(); }} placeholder="e.g. 3.5" data-testid="player-dupr-rating" />
                </div>
              </>
            )}
            {showField('tshirt') && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">T-shirt Size {isMandatory('tshirt') && <span className="text-red-500">*</span>}{isMissing('tshirt_size') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                <Select value={extra.tshirt_size || ''} onValueChange={(v) => { setExtra({ ...extra, tshirt_size: v }); trackStarted(); }}>
                  <SelectTrigger className={fieldCls(isMissing('tshirt_size'))} data-testid="player-tshirt"><SelectValue placeholder="Size" /></SelectTrigger>
                  <SelectContent>{(masters.tshirt_sizes || TSHIRT_SIZES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showField('food') && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Food Preference {isMandatory('food') && <span className="text-red-500">*</span>}{isMissing('food_preference') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                <Select value={extra.food_preference || ''} onValueChange={(v) => { setExtra({ ...extra, food_preference: v }); trackStarted(); }}>
                  <SelectTrigger className={fieldCls(isMissing('food_preference'))} data-testid="player-food"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{FOOD_PREFS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showField('self_rating') && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">Self Rating {isMandatory('self_rating') && <span className="text-red-500">*</span>}{isMissing('self_rating') && <AlertCircle className="w-3 h-3 text-red-500" />}</Label>
                <Select value={extra.self_rating || ''} onValueChange={(v) => { setExtra({ ...extra, self_rating: v }); trackStarted(); }}>
                  <SelectTrigger className={fieldCls(isMissing('self_rating'))} data-testid="player-self-rating"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(masters.self_ratings || SELF_RATINGS).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          {submitAttempted && missing.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="validation-error-banner">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Please complete all required fields highlighted in red before registering.
            </div>
          )}
        </div>

        {/* Partner — one card per selected doubles/mixed category */}
        {partnerCats.map((c) => (
          <PartnerCard key={c.id} category={c} partner={partners[c.id] || emptyPartner()}
            onChange={(updater) => setPartnerFor(c.id, updater)} />
        ))}

        {/* Coupon */}
        {event.allow_coupons && (
          <div className="bg-white rounded-2xl shadow-sm p-4 space-y-2">
            <h2 className="font-semibold text-slate-800 flex items-center gap-1.5"><Tag className="w-4 h-4 text-emerald-600" /> Coupon Code</h2>
            {(() => {
              const c = event.coupon;
              if (!c || !c.code || c.is_active === false) return null;
              const today = todayIST();
              const inWindow = (!c.valid_from || today >= c.valid_from) && (!c.valid_to || today <= c.valid_to);
              if (!inWindow) return null;
              const discountText = c.discount_type === 'flat' ? `₹${c.discount_value} off` : `${c.discount_value}% off`;
              return (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-700" data-testid="available-coupon-chip">
                  <Tag className="w-3.5 h-3.5" /> <span className="font-bold">{c.code}</span> — {discountText} <span className="text-emerald-500 ml-1">applied automatically ✓</span>
                </div>
              );
            })()}
            <p className="text-xs text-slate-400">Have a different code? Enter it below to override.</p>
            <div className="flex gap-2">
              <Input value={couponCode} onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }} placeholder="Enter code" data-testid="coupon-code-input" />
              <Button type="button" variant="outline" onClick={applyCoupon} disabled={applyingCoupon || !couponCode.trim()} data-testid="apply-coupon-btn">
                {applyingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
            {couponResult && (
              <div className={`text-xs flex items-center gap-1.5 ${couponResult.valid ? 'text-emerald-600' : 'text-red-500'}`} data-testid="coupon-result-message">
                {couponResult.valid ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {couponResult.message}
              </div>
            )}
          </div>
        )}

        {(event.custom_fields || []).some((f) => f.mode !== 'hidden') && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <CustomResponsesInputs fields={event.custom_fields} values={custom} onChange={setCustom}
              missing={submitAttempted ? missingCustom(event.custom_fields, custom) : []} testPrefix="player-cf" />
          </div>
        )}

        {/* Waiver */}
        {showField('waiver') && (
          <label className={`flex items-start gap-2 text-sm bg-white rounded-2xl shadow-sm p-4 cursor-pointer ${isMissing('waiver') ? 'ring-2 ring-red-400 border border-red-400' : ''}`}>
            <input type="checkbox" checked={waiver} onChange={(e) => setWaiver(e.target.checked)} className="mt-0.5" data-testid="player-waiver" />
            <span className="text-slate-600">I accept the tournament rules, terms &amp; liability waiver.{isMandatory('waiver') && <span className="text-red-500 ml-1 text-xs">*Required</span>}</span>
          </label>
        )}

        {event.payment_enabled && subtotal > 0 && (
          <div className="flex items-start gap-2 text-xs text-slate-500 px-1"><Info className="w-4 h-4 shrink-0 mt-0.5" /> You'll pay securely online. Full-category slots will be placed on waitlist.</div>
        )}
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-violet-100 p-4 shadow-2xl space-y-2">
        <div className="text-sm space-y-0.5">
          <div className="flex items-center justify-between text-slate-500">
            <span>{selected.length} categor{selected.length === 1 ? 'y' : 'ies'} — Subtotal</span>
            <span data-testid="summary-subtotal">₹{subtotal.toLocaleString('en-IN')}</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between text-emerald-600 font-medium" data-testid="summary-discount">
              <span>Discount ({couponCode})</span>
              <span>− ₹{discount.toLocaleString('en-IN')}</span>
            </div>
          )}
          <div className="flex items-center justify-between font-bold text-slate-900 text-base border-t pt-1">
            <span>Total</span>
            <span data-testid="summary-total" className="flex items-center"><IndianRupee className="w-4 h-4" />{total.toLocaleString('en-IN')}</span>
          </div>
        </div>
        <Button onClick={submit} disabled={submitting || (isAuthenticated && selected.length === 0)}
          className="w-full h-12 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white font-bold rounded-xl shadow-lg" data-testid="player-register-btn">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : !isAuthenticated ? 'Login to Register' : (event.payment_enabled && total > 0 ? `Register & Pay ₹${total}` : 'Register Now')}
        </Button>
      </div>
      <CustomerFooter />
    </div>
  );
}
